import React from "react";
import { View, Linking, Text } from "react-native";
import HTML from "react-native-render-html";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
  widthPercentageToDP,
} from "react-native-responsive-screen";
import WebView from "react-native-webview";
import theme from "app/Theme/Theme";
import { Box, ImageModal } from "app/components/common";
import removeHtmlTags from "@microdoing/shared/utils/removeHtmlTags";
import normalize from "app/utils/normalize";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";

const convertHtml = (html, isContentTitle) => {
  if (!isContentTitle) return html;

  const withoutHtml = removeHtmlTags(html);
  const firstWorld = withoutHtml?.split(" ")?.[0];
  const firstWorldWithUpperCase =
    firstWorld?.charAt(0)?.toUpperCase() + firstWorld?.slice(1);

  const newHtml = html.replace(
    firstWorld,
    `<b style="color:${theme.colors["brand.500"]}">${firstWorldWithUpperCase}</b>`,
  );

  return newHtml;
};

const renderers = {
  img: htmlAttribs => {
    let { src } = htmlAttribs;
    return (
      <Box marginBottom="m" alignSelf="center" marginTop="xs" width="100%">
        <ImageModal
          style={styles.image}
          modalImageStyle={styles.image}
          source={{ uri: src }}
        />
      </Box>
    );
  },

  iframe: htmlAttribs => {
    const { src } = htmlAttribs;

    if (isWebDesktop) {
      return (
        <div
          style={{
            flex: 0,
            alignSelf: "center",
            alignContent: "center",
            width: "100%",
            minWidth: "100%",
            height: "auto",
            minHeight: 250,
            borderRadius: 4,
            opacity: 0.99,
            maxWidth: "100%",
            marginTop: 10,
            marginBottom: 10,
          }}
        >
          <iframe
            {...htmlAttribs}
            style={{ width: "100%", height: "100%", borderRadius: 4 }}
            allowfullscreen="allowfullscreen"
            mozallowfullscreen="mozallowfullscreen"
            msallowfullscreen="msallowfullscreen"
            oallowfullscreen="oallowfullscreen"
            webkitallowfullscreen="webkitallowfullscreen"
            allow="fullscreen;"
          />
        </div>
      );
    }

    return (
      <WebView
        mixedContentMode="always"
        originWhitelist={["*"]}
        automaticallyAdjustContentInsets={false}
        containerStyle={{ flex: 0, height: "auto" }}
        key={src}
        style={{
          flex: 0,
          alignSelf: "center",
          alignContent: "center",
          width: wp("100%"),
          height: "auto",
          minHeight: 200,
          borderRadius: 4,
          opacity: 0.99,
          maxWidth: "100%",
          marginTop: 10,
          marginBottom: 10,
        }}
        frameborder="0"
        allow="autoplay; encrypted-media"
        allowsFullscreenVideo
        useWebKit
        scalesPageToFit={false}
        allowsInlineMediaPlayback
        mediaPlaybackRequiresUserAction
        javaScriptEnabled
        scrollEnabled={true}
        source={{
          uri: `${src}?showinfo=0`,
        }}
      />
    );
  },
};

const tagsStyles = {
  a: {
    lineHeight: 1.4 * normalize(14),
    fontSize: normalize(14),
  },
  h1: {
    lineHeight: 28,
  },
  blockquote: {
    backgroundColor: "#e2f2ff",
    borderLeftWidth: 4,
    borderLeftColor: "#4ca8de",
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 15,
    marginTop: 10,
    marginBottom: 10,
  },
  figure: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
  },
  figcaption: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    width: "100%",
    textAlign: "center",
    marginBottom: 10,
    fontSize: wp("3.5%"),
  },
  iframe: {
    width: widthPercentageToDP("100%"),
    height: "56.25%",
    borderWidth: 1,
    borderRadius: 8,
    marginBottom: 5,
  },
  img: {
    borderRadius: 8,
    marginBottom: 5,
  },
  ul: {
    paddingTop: 0,
    paddingBottom: 0,
    marginBottom: 0,
    marginTop: 0,
  },

  p: {
    // ...theme.body["l3-regular"],
    fontWeight: "400",
    lineHeight: 1.4 * normalize(14),
    fontSize: normalize(14),
    // fontSize: wp(4),
  },
  strong: {
    fontWeight: "600",
    lineHeight: 1.4 * normalize(14),
    fontSize: normalize(14),
    // fontSize: wp(4),
  },
};

const classesStyles = {
  "md-inline-highlight": { backgroundColor: "yellow" },
  "md-inline-italic": {
    // ...theme.body["l3-regular"],
    fontSize: normalize(14),
  },
  "md-inline-bold": {
    // ...theme.body["l3-bold"],
    fontWeight: "600",
    fontSize: normalize(14),
  },
};

const customWrapper = (content, onLayout) => (
  <View style={styles.actionView}>{content}</View>
);

const ActionText = ({
  text,
  textStyle,
  isContentTitle = false,
  numberOfLines = undefined,
}) => {
  // let finalText = text;
  if (text === '<p class="md-block-unstyled"><br/></p>') {
    return null;
  }

  // if (numberOfLines) {
  //   const inputString = text;
  //   const firstParagraphRegex = /<p>(.*?)<\/p>/s;
  //   const firstParagraphMatch = inputString.match(firstParagraphRegex);

  //   if (firstParagraphMatch) {
  //     const firstParagraph = firstParagraphMatch[0];
  //     finalText = firstParagraph;
  //   }
  // }

  return (
    <HTML
      onLinkPress={(event, href) => {
        Linking.openURL(href);
      }}
      customWrapper={customWrapper}
      renderers={renderers}
      tagsStyles={tagsStyles}
      classesStyles={classesStyles}
      allowFontScaling={false}
      baseFontStyle={{
        ...styles.actionText,
        // ...theme.body["l3-regular"],
        ...textStyle,
      }}
      html={convertHtml(text, isContentTitle)}
      imagesMaxWidth={wp("95%") - 30}
      alterNode={node => {
        const { name, parent, children } = node;
        if (
          name === "div" &&
          children &&
          children.length &&
          children[0].name === "iframe"
        ) {
          // Prevent iframe parent div from having an absolute position or style that prevent rendering
          node.attribs.style = "width: 100%; height: auto; border-radius: 8px;";
          return node;
        }
        // Don't return anything (eg a falsy value) for anything else so nothing is altered
      }}
    />
  );
};

const styles = {
  actionText: {
    // ...theme.body["l3-regular"],
    fontWeight: "400",
    lineHeight: 1.4 * normalize(14),
    fontSize: normalize(14),
    marginTop: 0,
    flex: 1,
    flexWrap: "wrap",
    flexShrink: 1,
  },
  actionView: {
    width: "100%",
    borderRadius: 8,
    borderWidth: 0,
    paddingTop: 5,
  },
  image: {
    borderRadius: 8,
  },
};

ActionText.defaultProps = {
  text: "",
  textStyle: {},
};

export default ActionText;
